import React, { useEffect, useRef, useCallback, useState } from 'react';
import { PublicClientApplication } from "@azure/msal-browser";
import { getB2CRedirectLogoutUrl } from "./UrlConstants";
import { toast } from 'react-toastify';
import { setLogoutInProgress } from "./authUtils";
import {msalConfig} from "./../component/auth/authConfig";

// Create MSAL instance outside component
const msalInstance = new PublicClientApplication(msalConfig);

export const userActivityLogout = (Component: React.ComponentType<any>) => {
    return (props: any) => {
        const timerRef = useRef<number | undefined>(undefined);
        const alertRef = useRef(false);
        const toastIdRef = useRef<any>(null);
        const [showOverlay, setShowOverlay] = useState(false);
        const isLoggingOutRef = useRef(false);
        const [msalReady, setMsalReady] = useState(false);

        // Track tokens in state to detect changes
        const [internalToken, setInternalToken] = useState<string | null>(null);
        const [externalToken, setExternalToken] = useState<string | null>(null);

        // Initialize MSAL instance
        useEffect(() => {
            msalInstance.initialize().then(() => {
                setMsalReady(true);
            }).catch(err => {
                console.error("MSAL initialization failed:", err);
            });
        }, []);

        // Check for tokens on mount and listen for changes
        useEffect(() => {
            const checkTokens = () => {
                const storedInternalToken = sessionStorage.getItem("internal_token");
                const storedExternalToken = sessionStorage.getItem("external_token");

                if (storedInternalToken !== internalToken) {
                    setInternalToken(storedInternalToken);
                }
                if (storedExternalToken !== externalToken) {
                    setExternalToken(storedExternalToken);
                }
            };

            checkTokens();

            // Check every 30 seconds
            const interval = setInterval(checkTokens, 30000);

            window.addEventListener('storage', checkTokens);

            const handleCustomStorageEvent = () => {
                checkTokens();
            };

            window.addEventListener('sessionStorageUpdate', handleCustomStorageEvent as EventListener);

            return () => {
                clearInterval(interval);
                window.removeEventListener('storage', checkTokens);
                window.removeEventListener('sessionStorageUpdate', handleCustomStorageEvent as EventListener);
            };
        }, [internalToken, externalToken]);

        // Check if user is authenticated (has either token)
        const isAuthenticated = Boolean(internalToken || externalToken);
        const isInternalUser = Boolean(internalToken);
        const isExternalUser = Boolean(externalToken);

        const handleLogout = useCallback(async () => {
            isLoggingOutRef.current = true;
            setShowOverlay(false);

            setLogoutInProgress(true);

            if (isInternalUser) {
                // Logout for internal (AD) user
                sessionStorage.removeItem("internal_token");
                sessionStorage.removeItem("msalToken");
                sessionStorage.removeItem("sessionExpired");
                alertRef.current = false;
                console.log("Logging out internal user");

                try {
                    if (msalReady) {
                        const accounts = msalInstance.getAllAccounts();
                        if (accounts && accounts.length > 0) {
                            await msalInstance.logoutRedirect({
                                account: accounts[0],
                                postLogoutRedirectUri: "/",
                            });
                        } else {
                            console.warn("No MSAL accounts found, redirecting to home");
                            window.location.href = "/";
                        }
                    } else {
                        console.warn("MSAL not ready, redirecting to home");
                        window.location.href = "/";
                    }
                } catch (error) {
                    console.error("MSAL logout failed:", error);
                    window.location.href = "/";
                }
            } else if (isExternalUser) {
                // Logout for external (B2C) user
                sessionStorage.removeItem("external_token");
                sessionStorage.removeItem("external_groups");
                sessionStorage.removeItem("signedInUserType");
                sessionStorage.removeItem("sessionExpired");
                alertRef.current = false;
                window.location.href = getB2CRedirectLogoutUrl();
            }
        }, [isInternalUser, isExternalUser, msalReady]);

        const handleAlert = useCallback(async () => {
            if (isAuthenticated && !alertRef.current && !toastIdRef.current) {
                alertRef.current = true;
                setShowOverlay(true);

                // Mark session as expired in sessionStorage
                sessionStorage.setItem('sessionExpired', new Date().toISOString());

                const toastId = toast.warn(
                    <div style={{ position: 'relative', width: '300px' }}>
                        <div style={{
                            marginBottom: '15px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#822405ff',
                            borderBottom: '1px solid #ffeaa7',
                            paddingBottom: '10px'
                        }}>
                            Session Expired
                        </div>
                        <div style={{ marginBottom: '15px', fontSize: '14px', lineHeight: '1.5', color: '#822405ff' }}>
                            <div style={{ marginBottom: '5px' }}>Your session has expired due to inactivity.</div>
                            <div>You need to login again to start over.</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <button
                                onClick={() => {
                                    toast.dismiss(toastId);
                                    toastIdRef.current = null;
                                    setShowOverlay(false);
                                    handleLogout();
                                }}
                                style={{
                                    padding: '8px 20px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    position: 'relative',
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>,
                    {
                        autoClose: false,
                        closeOnClick: false,
                        closeButton: false,
                        position: "top-center",
                        style: {
                            pointerEvents: 'auto'
                        },
                        onClose: () => {
                            toastIdRef.current = null;
                            alertRef.current = false;
                            setShowOverlay(false);
                        }
                    }
                );

                toastIdRef.current = toastId;
            }
        }, [handleLogout, isAuthenticated]);

        // Prevent keyboard/context menu when overlay is shown
        useEffect(() => {
            if (showOverlay) {
                const preventKeyboard = (e: KeyboardEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                };

                const preventContextMenu = (e: MouseEvent) => {
                    e.preventDefault();
                    return false;
                };

                const preventUnload = (e: BeforeUnloadEvent) => {
                    if (isLoggingOutRef.current) {
                        return;
                    }
                    e.preventDefault();
                    e.returnValue = '';
                    return '';
                };

                document.addEventListener('keydown', preventKeyboard, true);
                document.addEventListener('contextmenu', preventContextMenu, true);
                window.addEventListener('beforeunload', preventUnload, true);

                return () => {
                    document.removeEventListener('keydown', preventKeyboard, true);
                    document.removeEventListener('contextmenu', preventContextMenu, true);
                    window.removeEventListener('beforeunload', preventUnload, true);
                };
            }
        }, [showOverlay]);

        // Check if session was already expired (e.g., after page refresh)
        useEffect(() => {
            const sessionExpired = sessionStorage.getItem('sessionExpired');
            if (sessionExpired && isAuthenticated) {
                console.log("Session already expired, forcing logout");
                handleLogout();
            }
        }, [isAuthenticated, handleLogout]);

        // Activity monitoring and timer
        useEffect(() => {
            alertRef.current = false;
            toastIdRef.current = null;
            setShowOverlay(false);

            const resetTimer = () => {
                if (alertRef.current || toastIdRef.current) {
                    console.log("Alert/Toast already active, not resetting timer");
                    return;
                }

                if (timerRef.current !== undefined) {
                    clearTimeout(timerRef.current);
                }

                timerRef.current = window.setTimeout(handleAlert, 900000); // 15 minutes
            };

            if (isAuthenticated) {
                const events = ['mousemove', 'keydown', 'scroll', 'click'];

                events.forEach(event => {
                    window.addEventListener(event, resetTimer);
                });

                resetTimer();

                return () => {
                    if (timerRef.current !== undefined) {
                        clearTimeout(timerRef.current);
                    }
                    events.forEach(event => {
                        window.removeEventListener(event, resetTimer);
                    });

                    if (toastIdRef.current) {
                        toast.dismiss(toastIdRef.current);
                        toastIdRef.current = null;
                    }
                    setShowOverlay(false);
                };
            } else {
                console.log("No active user session, skipping activity monitoring");
            }
        }, [handleAlert, isAuthenticated]);

        return (
            <>
                <Component {...props} />
                {showOverlay && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    />
                )}
            </>
        );
    };
};